import React from 'react';
import { X, Shield, FileText, RefreshCcw, Info, PhoneCall } from 'lucide-react';

export type PolicyType = 'refund' | 'terms' | 'privacy' | 'about' | 'contact';

interface PolicyModalProps {
  isOpen: boolean;
  onClose: () => void;
  type: PolicyType;
}

export const PolicyModal: React.FC<PolicyModalProps> = ({ isOpen, onClose, type }) => {
  if (!isOpen) return null;

  const getPolicyContent = () => {
    switch (type) {
      case 'refund':
        return {
          title: 'Refund and Cancellation Policy',
          icon: <RefreshCcw className="text-orange-600" size={32} />,
          content: (
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p className="font-medium text-gray-900 italic underline">Last updated on 21-03-2026</p>
              <p className="font-medium text-gray-900">At BR TEACHER, we strive to provide the best resources for the teaching community in Bihar.</p>
              
              <div>
                <h4 className="font-bold text-gray-900 mb-1">Cancellations:</h4>
                <p>Users can cancel their subscription/service request within 24 hours of payment, provided the digital service/resource has not been accessed or downloaded. Once a digital product is accessed, cancellation is not possible.</p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-1">Refunds:</h4>
                <p>Since we primarily provide digital content and consultancy services, refunds are processed only in cases of accidental double payments or technical failures where the service was not delivered. No refunds will be provided for digital downloads once the link is shared or accessed.</p>
              </div>

              <div>
                <h4 className="font-bold text-gray-900 mb-1">Timeline:</h4>
                <p>Approved refunds will be credited back to the original payment method (UPI/Bank Account) within 5-7 working days.</p>
              </div>

              <div className="bg-orange-50 p-4 rounded-xl border border-orange-100 mt-6">
                <h4 className="font-bold text-orange-900 mb-1">Contact for Queries:</h4>
                <p className="text-orange-800">For refund queries, please contact us at <a href="mailto:Support@brteacher.in" className="font-bold underline">Support@brteacher.in</a></p>
              </div>
            </div>
          )
        };
      case 'terms':
        return {
          title: 'Terms and Conditions',
          icon: <FileText className="text-blue-600" size={32} />,
          content: (
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p className="font-medium text-gray-900 italic underline">Last updated on 21-03-2026</p>
              <p className="font-medium text-gray-900">Welcome to BR TEACHER (brteacher.in). By using this website, you agree to:</p>
              
              <ul className="list-disc pl-5 space-y-3">
                <li><span className="font-bold text-gray-900">Usage:</span> The content provided is for informational purposes for teachers in Bihar. You agree not to misuse the calculators or resources.</li>
                <li><span className="font-bold text-gray-900">Payments:</span> All payments made via our secure gateway are final. You agree to provide accurate payment details.</li>
                <li><span className="font-bold text-gray-900">Account Responsibility:</span> If you create an account, you are responsible for maintaining the confidentiality of your credentials.</li>
                <li><span className="font-bold text-gray-900">Limitation of Liability:</span> BR TEACHER is not responsible for any technical issues arising from third-party payment processors or internet connectivity.</li>
                <li><span className="font-bold text-gray-900">Jurisdiction:</span> Any disputes will be subject to the courts in Gopalganj, Bihar.</li>
              </ul>
            </div>
          )
        };
      case 'privacy':
        return {
          title: 'Privacy Policy',
          icon: <Shield className="text-emerald-600" size={32} />,
          content: (
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p className="font-medium text-gray-900 italic underline">Last updated on 21-03-2026</p>
              <p className="font-medium text-gray-900">Your privacy is crucial to us, especially when handling payment data.</p>
              
              <div className="space-y-4">
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Data Collection:</h4>
                  <p>We collect minimal data (Name, Email, Phone Number) required for account creation and payment processing. We do not collect sensitive personal data unless necessary.</p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Data Security:</h4>
                  <p>We do not store your UPI PIN or Bank details. All transactions are processed securely by our payment partners (Razorpay/PhonePe).</p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Third Parties:</h4>
                  <p>Your data is never sold. It is only shared with the payment gateway to complete your transaction and for legal compliance.</p>
                </div>
              </div>
            </div>
          )
        };
      case 'about':
        return {
          title: 'About Us',
          icon: <Info className="text-indigo-600" size={32} />,
          content: (
            <div className="space-y-4 text-gray-600 leading-relaxed">
              <p className="font-medium text-gray-900">BR TEACHER is Bihar's premier resource portal dedicated to the teaching community, specifically focusing on BPSC TRE candidates and appointed teachers.</p>
              
              <div className="space-y-4">
                <p>Founded with the mission to simplify complex administrative tasks for teachers, we provide automated tools like Arrear Calculators, Salary Checkers, and TDS forms. Our goal is to ensure that every teacher in Bihar has access to accurate information and resources at their fingertips.</p>
                
                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Our Vision:</h4>
                  <p>To become the most trusted digital companion for Bihar's educators, empowering them with technology and reliable data.</p>
                </div>

                <div>
                  <h4 className="font-bold text-gray-900 mb-1">Our Commitment:</h4>
                  <p>We are committed to transparency, accuracy, and continuous improvement based on the feedback from our vibrant community of teachers.</p>
                </div>
              </div>
            </div>
          )
        };
      case 'contact':
        return {
          title: 'Contact Us',
          icon: <PhoneCall className="text-rose-600" size={32} />,
          content: (
            <div className="space-y-6 text-gray-600 leading-relaxed">
              <p className="font-medium text-gray-900">We are here to help you. Reach out to us for any queries or support.</p>
              
              <div className="grid grid-cols-1 gap-6">
                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-3 uppercase text-xs tracking-widest">Business Details</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-500">Business Name:</span>
                      <span className="font-bold text-gray-900">BRTEACHER</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-500">Address:</span>
                      <span className="font-bold text-gray-900 text-right">RAJENDRA NAGAR, GOPALGANJ (BIHAR) INDIA</span>
                    </div>
                  </div>
                </div>

                <div className="bg-gray-50 p-5 rounded-2xl border border-gray-100">
                  <h4 className="font-bold text-gray-900 mb-3 uppercase text-xs tracking-widest">Support Channels</h4>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Email 1:</span>
                      <a href="mailto:SUPPORT.BRTEACHER@gmail.com" className="font-bold text-orange-600 underline">SUPPORT.BRTEACHER@gmail.com</a>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Email 2:</span>
                      <a href="mailto:Support@brteacher.in" className="font-bold text-orange-600 underline">Support@brteacher.in</a>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-500">Phone:</span>
                      <a href="tel:9229001900" className="font-bold text-gray-900">+91 9229001900</a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )
        };
      default:
        return { title: '', icon: null, content: null };
    }
  };

  const { title, icon, content } = getPolicyContent();

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fadeIn">
      <div className="bg-white rounded-[2rem] shadow-2xl w-full max-w-2xl overflow-hidden transform transition-all scale-100 relative max-h-[90vh] flex flex-col">
        <button 
          onClick={onClose}
          className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 transition p-2 bg-gray-100 rounded-full z-10"
        >
          <X size={20} />
        </button>

        <div className="p-8 border-b border-gray-100 flex items-center">
          <div className="mr-4 p-3 bg-gray-50 rounded-2xl">
            {icon}
          </div>
          <div>
            <h3 className="text-2xl font-black text-gray-900">{title}</h3>
            <p className="text-gray-500 text-sm font-medium">Last updated: March 2026</p>
          </div>
        </div>
        
        <div className="p-8 overflow-y-auto">
          {content}
        </div>

        <div className="p-6 bg-gray-50 border-t border-gray-100 flex justify-end">
          <button 
            onClick={onClose}
            className="px-8 py-3 bg-gray-900 text-white font-bold rounded-xl hover:bg-gray-800 transition shadow-lg"
          >
            I Understand
          </button>
        </div>
      </div>
    </div>
  );
};
